/**
 * 화질 정책의 기준이 되는 디바이스 등급 판별.
 * JVB 대역폭 추정(BWE)이 양호한 환경에서도 오강등을 일으켜 신뢰할 수 없으므로,
 * 디바이스 능력을 기준으로 수신/송신 화질 상한을 클라이언트가 직접 결정한다.
 */
// 송신(1080 3계층 동시 인코딩)용 최소 코어 — 인코딩이 무거우므로 보수적으로.
// 기준 6: 실측 기준 강사용 데스크톱(6C/32GB)이 통과해야 한다.
// hardwareConcurrency는 논리 프로세서 수라 6C/6T 데스크톱은 6으로 보고된다.
const MIN_ENCODE_CPU_CORES = 6;
// 수신(1080 1스트림 디코딩)용 최소 코어 — 디코딩은 인코딩의 1/3~1/10 수준으로 가볍고,
// 오판 시 receiveQualityMonitor가 720으로 자동 강등하는 안전망이 있어 완화한다.
const MIN_DECODE_CPU_CORES = 4;
const MIN_DEVICE_MEMORY_GB = 8;
const MIN_SCREEN_HEIGHT = 1080;
export const RECEIVE_VIDEO_HEIGHT = {
    HIGH: 1080,
    STANDARD: 720,
};
/**
 * 1080p 처리를 감당할 수 있는 디바이스인가.
 * 판별이 불가능한 환경(native 등)에서는 보수적으로 false.
 * @param {number} minCores
 * @returns {boolean}
 */
function checkDeviceSpec(minCores) {
    if (typeof navigator === 'undefined' || typeof window === 'undefined')
        return false;
    // 모바일/태블릿은 코어 수가 많아도(빅리틀 8코어 등) 제외 — 화면이 작아 1080 수신의 이득이 없다
    if (/Android|iPhone|iPad|Mobile/i.test(navigator.userAgent || ''))
        return false;
    const cores = navigator.hardwareConcurrency;
    if (!cores || cores < minCores)
        return false;
    // deviceMemory는 Chrome 계열만 노출(최대 8로 캡). 미노출 브라우저는 감점하지 않는다.
    const memory = navigator.deviceMemory;
    if (memory !== undefined && memory < MIN_DEVICE_MEMORY_GB)
        return false;
    // 화면이 1080 라인을 표시하지 못하면 1080p 수신은 디코딩 낭비다.
    const screenHeight = (window.screen?.height || 0) * (window.devicePixelRatio || 1);
    if (screenHeight < MIN_SCREEN_HEIGHT)
        return false;
    return true;
}
/**
 * 송신용 고사양 판별 (1080 캡처 + 3계층 인코딩 허용 여부)
 * @returns {boolean}
 */
export function isHighSpecSender() {
    return checkDeviceSpec(MIN_ENCODE_CPU_CORES);
}
/**
 * 수신용 고사양 판별 (1080 수신 + assumedBandwidthBps 적용 여부)
 * @returns {boolean}
 */
export function isHighSpecReceiver() {
    return checkDeviceSpec(MIN_DECODE_CPU_CORES);
}
/**
 * 이 디바이스의 기본 수신 해상도 상한 (고사양 1080 / 그 외 720)
 * @returns {number}
 */
export function getDefaultReceiveMaxHeight() {
    return isHighSpecReceiver() ? RECEIVE_VIDEO_HEIGHT.HIGH : RECEIVE_VIDEO_HEIGHT.STANDARD;
}
/**
 * 카메라 캡처 높이. 1080 캡처(3계층 시뮬캐스트 인코딩)는 부담이 크므로
 * 호스트이면서 고사양인 경우에만 1080, 그 외에는 기존과 동일한 720.
 * @param {boolean} isHost
 * @returns {number}
 */
export function getCameraCaptureHeight(isHost) {
    return isHost && isHighSpecSender() ? 1080 : 720;
}
/**
 * 게이트 판별 근거 (화질 이슈 현장 진단용)
 * @returns {Object}
 */
export function getVideoCapabilityDebugInfo() {
    const hasBrowserEnv = typeof navigator !== 'undefined' && typeof window !== 'undefined';
    return {
        highSpecSender: isHighSpecSender(),
        highSpecReceiver: isHighSpecReceiver(),
        cores: hasBrowserEnv ? navigator.hardwareConcurrency : undefined,
        memoryGB: hasBrowserEnv ? navigator.deviceMemory : undefined,
        screenHeight: hasBrowserEnv ? (window.screen?.height || 0) * (window.devicePixelRatio || 1) : undefined,
    };
}
